<script lang="ts">
  import {
    type CalendarEvent,
    type LaneAssignable,
    CalendarCellInteraction,
    getSpanTypeClass,
    assignLanes,
    maxLaneCount,
    eventEndDate,
    eventTotalDays,
    isMultiDayEvent,
    eventColor,
    formatEventTimeShort,
    openEventModalForEdit,
    openEventModal,
    MONTH_NAMES,
    getTodayStr,
  } from '../lib/calendarViewHelpers.svelte';
  import { parseCalendarDate, toDateStr, formatCalendarDate } from '../lib/utils';
  import { draggedEvent, requestEventMove } from '../lib/stores/ui';
  import { viewport } from '../lib/stores/viewport';

  interface Props {
    events: CalendarEvent[];
    startDate: string;
    range: number;
  }
  let { events, startDate, range }: Props = $props();
  const ci = new CalendarCellInteraction(() => events);
  let dropTargetCell = $state<string | null>(null);
  let dropTargetTopDate = $state<string | null>(null);

  interface DayCol {
    date: Date;
    dateStr: string;
    weekday: string;
    dayLabel: string;
    isToday: boolean;
  }

  interface SpanEvent extends LaneAssignable {
    event: CalendarEvent;
    continuesLeft: boolean;
    continuesRight: boolean;
  }

  type BlockKey = 'morning' | 'midday' | 'afternoon' | 'evening' | 'late-night';
  const BLOCKS: { key: BlockKey; label: string; subtitle: string }[] = [
    { key: 'morning', label: 'Morning', subtitle: '6a-10a' },
    { key: 'midday', label: 'Midday', subtitle: '10a-2p' },
    { key: 'afternoon', label: 'Afternoon', subtitle: '2p-6p' },
    { key: 'evening', label: 'Evening', subtitle: '6p-10p' },
    { key: 'late-night', label: 'Late Night', subtitle: '10p-6a' },
  ];

  const days = $derived.by(() => {
    const start = parseCalendarDate(startDate);
    const todayStr = getTodayStr();
    return Array.from({ length: range }, (_, i) => {
      const d = new Date(start);
      d.setUTCDate(d.getUTCDate() + i);
      const dateStr = toDateStr(d);
      return {
        date: d,
        dateStr,
        weekday: formatCalendarDate(d, { weekday: 'short' }).toUpperCase(),
        dayLabel: formatCalendarDate(d, { month: 'short', day: 'numeric' }),
        isToday: dateStr === todayStr,
      } as DayCol;
    });
  });

  const endDate = $derived(days[days.length - 1]?.dateStr || startDate);

  const allDaySingleDaySpans = $derived.by(() => {
    const spans: SpanEvent[] = events
      .filter(
        (e) => !e.startTime && !isMultiDayEvent(e) && e.date >= startDate && e.date <= endDate,
      )
      .map((e) => {
        const idx = days.findIndex((d) => d.dateStr === e.date);
        return {
          event: e,
          startCol: idx,
          endCol: idx,
          lane: 0,
          continuesLeft: false,
          continuesRight: false,
        };
      })
      .filter((e) => e.startCol >= 0);

    return assignLanes(spans);
  });

  const multiDaySpans = $derived.by(() => {
    const spans: SpanEvent[] = events
      .filter((e) => isMultiDayEvent(e) && e.date <= endDate && eventEndDate(e) >= startDate)
      .map((e) => {
        const start = parseCalendarDate(e.date);
        const end = parseCalendarDate(eventEndDate(e));
        const viewStart = parseCalendarDate(startDate);
        const viewEnd = parseCalendarDate(endDate);

        const startCol = Math.max(
          0,
          Math.round((start.getTime() - viewStart.getTime()) / 86_400_000),
        );
        const endCol = Math.min(
          range - 1,
          Math.round((end.getTime() - viewStart.getTime()) / 86_400_000),
        );

        return {
          event: e,
          startCol,
          endCol,
          lane: 0,
          continuesLeft: start < viewStart,
          continuesRight: end > viewEnd,
        };
      });

    return assignLanes(spans);
  });

  // A bar is a tap target: 44px rows on phones, the compact 16px on desktop.
  const laneHeight = $derived($viewport.isMobile ? 44 : 16);
  const spanRows = $derived(maxLaneCount(multiDaySpans) + maxLaneCount(allDaySingleDaySpans));

  function defaultStartTimeForBlock(block: BlockKey): string {
    switch (block) {
      case 'morning':
        return '08:00';
      case 'midday':
        return '12:00';
      case 'afternoon':
        return '15:00';
      case 'evening':
        return '19:00';
      case 'late-night':
        return '22:00';
      default:
        return '09:00';
    }
  }

  function hoverLabel(day: DayCol, block: BlockKey): string {
    const blockLabel = BLOCKS.find((b) => b.key === block)?.label || 'Block';
    return `${day.weekday} ${MONTH_NAMES[day.date.getUTCMonth()]} ${day.date.getUTCDate()} · ${blockLabel}`;
  }

  function bucketFor(event: CalendarEvent): { dateStr: string; block: BlockKey } | null {
    if (!event.startTime) return null;
    const [h, m] = event.startTime.split(':').map(Number);
    const mins = h * 60 + m;

    let block: BlockKey;
    if (mins >= 6 * 60 && mins < 10 * 60) block = 'morning';
    else if (mins >= 10 * 60 && mins < 14 * 60) block = 'midday';
    else if (mins >= 14 * 60 && mins < 18 * 60) block = 'afternoon';
    else if (mins >= 18 * 60 && mins < 22 * 60) block = 'evening';
    else block = 'late-night';

    const baseDate = parseCalendarDate(event.date);
    if (mins < 6 * 60) baseDate.setUTCDate(baseDate.getUTCDate() - 1);

    return { dateStr: toDateStr(baseDate), block };
  }

  function cellKey(dateStr: string, block: BlockKey): string {
    return `${dateStr}::${block}`;
  }

  function getDraggedEventObj(): CalendarEvent | null {
    const id = $draggedEvent;
    if (!id) return null;
    return events.find((ev) => ev.id === id) || null;
  }

  function handleCellDrop(e: DragEvent, dateStr: string, block: BlockKey) {
    e.preventDefault();
    dropTargetCell = null;
    const eventId = e.dataTransfer?.getData('application/event');
    if (!eventId) return;
    const eventObj = events.find((ev) => ev.id === eventId);
    if (!eventObj || !eventObj.startTime) return;

    const currentBucket = bucketFor(eventObj);
    if (currentBucket && currentBucket.dateStr === dateStr && currentBucket.block === block) {
      return;
    }

    const targetStart = defaultStartTimeForBlock(block);
    requestEventMove(eventId, eventObj, 'day', {
      date: dateStr,
      startTime: targetStart,
    });
  }

  function handleTopDrop(e: DragEvent, dateStr: string) {
    e.preventDefault();
    dropTargetTopDate = null;
    const eventId = e.dataTransfer?.getData('application/event');
    if (!eventId) return;
    const eventObj = events.find((ev) => ev.id === eventId);
    if (!eventObj || eventObj.startTime) return;

    requestEventMove(eventId, eventObj, 'day', {
      date: dateStr,
      forceConfirm: true,
    });
  }

  function eventsForCell(dateStr: string, block: BlockKey): CalendarEvent[] {
    return events
      .filter((e) => {
        const b = bucketFor(e);
        return b && b.dateStr === dateStr && b.block === block;
      })
      .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
  }
</script>

<div class="time-blocks-wrap">
  <div class="grid" style="--cols: {range}; --spanRows: {spanRows}; --lane: {laneHeight}px;">
    <div class="corner"></div>
    {#each days as day}
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <div
        class="day-header"
        class:today={day.isToday}
        class:drop-target={dropTargetTopDate === day.dateStr}
        ondragover={(e) => {
          const dragged = getDraggedEventObj();
          if (!dragged || dragged.startTime) return;
          e.preventDefault();
          dropTargetTopDate = day.dateStr;
        }}
        ondragleave={() => {
          dropTargetTopDate = null;
        }}
        ondrop={(e) => handleTopDrop(e, day.dateStr)}
      >
        <div class="weekday">{day.weekday}</div>
        <div class="day">{day.dayLabel}</div>
      </div>
    {/each}

    <div class="span-area">
      <div class="top-drop-grid">
        {#each days as day}
          <!-- svelte-ignore a11y_no_static_element_interactions -->
          <div
            class="top-drop-cell"
            class:drop-target={dropTargetTopDate === day.dateStr}
            ondragover={(e) => {
              const dragged = getDraggedEventObj();
              if (!dragged || dragged.startTime) return;
              e.preventDefault();
              dropTargetTopDate = day.dateStr;
            }}
            ondragleave={() => {
              dropTargetTopDate = null;
            }}
            ondrop={(e) => handleTopDrop(e, day.dateStr)}
          ></div>
        {/each}
      </div>
      {#each multiDaySpans as span (span.event.id)}
        <button
          class="spanning-bar {getSpanTypeClass(span.event.type)}"
          class:dragging={ci.draggingEventId === span.event.id}
          draggable={!$viewport.isMobile}
          ondragstart={(e) => ci.pillDragStart(e, span.event)}
          ondragend={ci.pillDragEnd}
          class:continues-left={span.continuesLeft}
          class:continues-right={span.continuesRight}
          style="top: {span.lane *
            laneHeight}px; left: calc({span.startCol} / {range} * 100%); width: calc({span.endCol -
            span.startCol +
            1} / {range} * 100%);"
          onclick={() => ci.spanClick(span.event)}
          title="{span.event.title} ({eventTotalDays(span.event)} days)"
        >
          {span.event.title}
        </button>
      {/each}
      {#each allDaySingleDaySpans as span (span.event.id)}
        <button
          class="spanning-bar {getSpanTypeClass(span.event.type)}"
          class:dragging={ci.draggingEventId === span.event.id}
          draggable={!$viewport.isMobile}
          ondragstart={(e) => ci.pillDragStart(e, span.event)}
          ondragend={ci.pillDragEnd}
          style="top: {(maxLaneCount(multiDaySpans) + span.lane) *
            laneHeight}px; left: calc({span.startCol} / {range} * 100%); width: calc(1 / {range} * 100%);"
          onclick={() => ci.spanClick(span.event)}
          title={span.event.title}
        >
          {span.event.title}
        </button>
      {/each}
    </div>

    {#each BLOCKS as block}
      <div class="row-label">
        <div>{block.label}</div>
        <small>{block.subtitle}</small>
      </div>
      {#each days as day}
        {@const cellEvents = eventsForCell(day.dateStr, block.key)}
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <div
          class="cell"
          class:today={day.isToday}
          class:drop-target={dropTargetCell === cellKey(day.dateStr, block.key)}
          onclick={() => openEventModal(day.dateStr, defaultStartTimeForBlock(block.key))}
          onmouseenter={(e) => ci.cellEnter(e, day.dateStr, hoverLabel(day, block.key), cellEvents)}
          onmouseleave={ci.cellLeave}
          ondragover={(e) => {
            const dragged = getDraggedEventObj();
            if (!dragged || !dragged.startTime) return;
            e.preventDefault();
            dropTargetCell = cellKey(day.dateStr, block.key);
          }}
          ondragleave={() => {
            dropTargetCell = null;
          }}
          ondrop={(e) => handleCellDrop(e, day.dateStr, block.key)}
        >
          {#each cellEvents as event (event.id)}
            {@const timeStr = formatEventTimeShort(event)}
            <button
              class="event-pill"
              style="border-left-color: {eventColor(event.type)}"
              class:dragging={ci.draggingEventId === event.id}
              draggable={!$viewport.isMobile}
              ondragstart={(e) => ci.pillDragStart(e, event)}
              ondragend={ci.pillDragEnd}
              onclick={(e) => {
                e.stopPropagation();
                openEventModalForEdit(event);
              }}
            >
              {#if timeStr}<span class="event-time">{timeStr}</span>{/if}{event.title}
            </button>
          {/each}
        </div>
      {/each}
    {/each}
  </div>
</div>

<style>
  @import '../lib/calendarCellStyles.css';

  .time-blocks-wrap {
    height: 100%;
    padding: 0.5rem;
    box-sizing: border-box;
    overflow: hidden;
  }
  .grid {
    height: 100%;
    display: grid;
    grid-template-columns: min-content repeat(var(--cols), minmax(0, 1fr));
    grid-template-rows: 2.5rem calc(var(--spanRows) * var(--lane)) repeat(5, minmax(0, 1fr));
    gap: 1px;
    background: #e5e7eb;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    overflow: hidden;
  }
  .corner,
  .day-header,
  .row-label,
  .cell,
  .span-area {
    background: white;
  }
  .corner {
    background: #f3f4f6;
  }
  .day-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 0.6875rem;
    background: #f3f4f6;
    border: 1px solid transparent;
  }
  .day-header.today {
    background: #eff6ff;
    box-shadow: inset 0 0 0 2px #3b82f6;
  }
  .day-header.drop-target {
    border-color: #3b82f6;
    background: #dbeafe;
  }
  .weekday {
    color: #6b7280;
    font-weight: 700;
  }
  .day {
    color: #374151;
    font-weight: 600;
  }
  .span-area {
    grid-column: 2 / -1;
    position: relative;
  }
  .top-drop-grid {
    position: absolute;
    inset: 0;
    display: grid;
    grid-template-columns: repeat(var(--cols), minmax(0, 1fr));
    z-index: 1;
  }
  .top-drop-cell {
    border: 1px solid transparent;
  }
  .top-drop-cell.drop-target {
    border-color: #3b82f6;
    background: rgba(219, 234, 254, 0.45);
  }
  .row-label {
    padding: 0.25rem 0.125rem;
    color: #374151;
    background: #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: center;
    writing-mode: vertical-lr;
    transform: rotate(180deg);
    gap: 0.25rem;
    overflow: hidden;
  }
  .row-label > div {
    font-size: 0.625rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    display: block;
  }
  .row-label small {
    color: #6b7280;
    font-size: 0.5625rem;
    font-weight: 600;
    line-height: 1;
    display: block;
  }
  .cell {
    padding: 0.1875rem;
    overflow: hidden;
    border: 1px solid transparent;
    cursor: pointer;
  }
  .cell.today {
    background: #eff6ff;
  }
  .cell:hover {
    border-color: #f59e0b;
  }
  .cell.drop-target {
    border-color: #3b82f6;
    background: #eff6ff;
  }
  .event-pill {
    display: block;
    width: 100%;
    text-align: left;
    margin-bottom: 2px;
    font-size: 0.5625rem;
    padding: 0.0625rem 0.25rem;
  }
  .event-time {
    margin-right: 0.2rem;
    color: #6b7280;
    font-weight: 600;
  }
  .spanning-bar {
    position: absolute;
    height: 14px;
    font-size: 0.5625rem;
    padding: 0 4px;
    z-index: 2;
  }

  /* Phones (ma-nvbz): 12px text and 44px targets for the three columns. */
  @media (max-width: 767px) {
    .time-blocks-wrap {
      padding-bottom: 0.5rem;
    }
    .cell {
      overflow-y: auto;
    }
    .event-pill {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      min-height: 44px;
      margin-bottom: 4px;
      padding: 0.25rem 0.375rem;
      font-size: 0.75rem;
      line-height: 1.25;
      white-space: normal;
      overflow-wrap: anywhere;
    }
    .spanning-bar {
      box-sizing: border-box;
      height: var(--lane);
      padding: 0 6px;
      border-bottom: 2px solid #fff;
      font-size: 0.8125rem;
    }
    .day-header {
      font-size: 0.75rem;
    }
  }
</style>
